import React from 'react';
import { Voicemail } from 'lucide-react';

const MAX_VOICEMAIL_CHARS = 1000;

const VoicemailDropTab = ({ agent, formData, setFormData }) => {
  const voicemailDropEnabled = formData.voicemail_drop_enabled ?? false;
  const voicemailMessage = formData.voicemail_message || '';

  const handleToggleVoicemailDrop = (e) => {
    e.preventDefault();
    const newEnabled = !voicemailDropEnabled;
    setFormData({
      ...formData,
      voicemail_drop_enabled: newEnabled,
      ...(newEnabled ? {} : { voicemail_message: '' })
    });
  };

  const handleMessageChange = (e) => {
    setFormData({
      ...formData,
      voicemail_message: e.target.value.slice(0, MAX_VOICEMAIL_CHARS)
    });
  };

  return (
    <div className="flex-1 overflow-y-auto min-h-0 p-6"
      style={{
        scrollBehavior: 'smooth',
        maxHeight: 'calc(100vh - 280px)'
      }}
    >
      <div className="max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-card/95 backdrop-blur-md border border-border/60 rounded-xl p-6 shadow-elegant">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-gradient-button flex items-center justify-center shadow-md">
              <Voicemail className="w-5 h-5 text-primary-foreground" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-foreground">Voicemail Drop</h3>
              <p className="text-sm text-muted-foreground">Leave a message when an outbound call reaches voicemail</p>
            </div>
          </div>

          {/* Enable/Disable Toggle */}
          <div className="flex items-center justify-between p-4 bg-muted/20 rounded-lg border border-border/50">
            <div className="min-w-0 pr-4">
              <label className="text-sm font-medium text-foreground">Enable Voicemail Drop</label>
              <p className="text-xs text-muted-foreground mt-1">
                When enabled, the agent detects that an outbound call was answered by an
                answering machine, waits for the greeting to finish, leaves the message
                below, and hangs up. Without it the agent tries to hold a conversation
                with the recording until the machine cuts it off.
              </p>
              <p className="text-xs text-muted-foreground mt-2">
                Applies to outbound calls only. Inbound and web test calls are unaffected.
              </p>
            </div>
            <button
              type="button"
              onClick={handleToggleVoicemailDrop}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
                voicemailDropEnabled ? 'bg-primary' : 'bg-muted'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 flex-shrink-0 transform rounded-full bg-white transition-transform ${
                  voicemailDropEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Message input - only when voicemail drop is enabled */}
          {voicemailDropEnabled && (
            <div className="mt-4 space-y-4">
              <div className="p-4 bg-muted/20 rounded-lg border border-border/50 space-y-3">
                <div>
                  <label className="text-sm font-medium text-foreground">Voicemail Message</label>
                  <p className="text-xs text-muted-foreground mt-1">
                    The exact words the agent speaks into the voicemail. This is spoken as
                    written, not rewritten by the model, so keep it short and complete.
                  </p>
                </div>

                <textarea
                  value={voicemailMessage}
                  onChange={handleMessageChange}
                  rows={4}
                  maxLength={MAX_VOICEMAIL_CHARS}
                  placeholder="Hi, this is Sarah calling from Acme Health about your appointment. Please call us back at 555-0142. Thank you."
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
                />

                <div className="flex items-center justify-between">
                  <p className="text-xs text-muted-foreground">
                    Aim for 15 to 25 seconds. Many systems cut recordings off after 30.
                  </p>
                  <span className="text-xs text-muted-foreground flex-shrink-0 ml-3">
                    {voicemailMessage.length}/{MAX_VOICEMAIL_CHARS}
                  </span>
                </div>

                {!voicemailMessage.trim() && (
                  <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                    <p className="text-xs text-amber-600 dark:text-amber-400">
                      Voicemail drop stays off until you enter a message. With no message the
                      agent behaves exactly as it does today.
                    </p>
                  </div>
                )}
              </div>

              {/* How detection works */}
              <div className="p-4 bg-muted/20 rounded-lg border border-border/50">
                <label className="text-sm font-medium text-foreground">How detection works</label>
                <ul className="text-xs text-muted-foreground mt-2 space-y-1.5 list-disc list-inside">
                  <li>Answering Machine Detection classifies the call the moment it is answered, usually in under a second.</li>
                  <li>The agent is held silent until that verdict lands, so it never talks over a recorded greeting.</li>
                  <li>On voicemail it waits for the greeting to finish, speaks this message, and hangs up.</li>
                  <li>If a real person answers, the call continues normally with your usual greeting.</li>
                  <li>Numbers that are disconnected or unreachable are hung up without leaving a message.</li>
                  <li>Calls that drop a voicemail are marked <span className="font-medium text-foreground">Voicemail Reached</span> in call history.</li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VoicemailDropTab;
